import { authorisedContact } from "../../config/contact";
import { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { NavLink } from "react-router-dom";
import { FaArrowRight, FaBriefcase, FaCheckCircle, FaGraduationCap, FaUserCheck } from "react-icons/fa";
import SiteHeader from "../../Components/Shared/SiteHeader";
import JobList from "../../Components/Career banner/JobList";
import PlacementWall from "../../Components/Career banner/PlacementWall";
import CampusFooter from "../../Components/Achivement Gallery/CampusFooter";
import "./CareerPage.css";
import CareerParticles from "./CareerParticles";
import GalleryEventSelect from "../../Components/Achivement Gallery/GalleryEventSelect";

const sections = [
  { name: "Student Result Verification", icon: FaGraduationCap, description: "Academic records and verification support." },
  { name: "Jobs", icon: FaBriefcase, description: "Explore openings and apply for your next opportunity." },
  { name: "Jobs Recruitment", icon: FaUserCheck, description: "Prepare for applications, interviews, and campus recruitment." },
  { name: "Placed Candidates", icon: FaCheckCircle, description: "Discover the students beginning their professional journeys." },
];

export default function CareerPage() {
  const [selectedSection, setSelectedSection] = useState("Jobs");
  const contentRef = useRef(null);
  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const current = sections.find((section) => section.name === selectedSection);
  const Icon = current.icon;

  useEffect(() => {
    const root = pageRef.current;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const groups = [
      [".career-hub-hero-copy > *", "fade-up"],
      [".career-hub-hero-aside > *", "fade-up"],
      [".career-hub-selector > div", "fade-up"],
      [".career-hub-content-heading > *, .job_filters_header > *, .job_filters_container > *, .job-list-header > *, .job-empty, .job-card, .placement-wall-title > *, .placement-slider", "fade-up"],
      [".career-verification-card > *, .career-recruitment-steps > article, .career-recruitment-action > *", "fade-up"],
      [".campus-footer__content, .campus-footer__column, .campus-footer__bottom", "fade-up"],
    ];
    let frame;
    function registerElements() {
      let changed = false;
      groups.forEach(([selector, animation]) => {
        root.querySelectorAll(selector).forEach((element, index) => {
          if (element.hasAttribute("data-aos")) return;
          element.setAttribute("data-aos", animation);
          element.setAttribute("data-aos-delay", String((index % 4) * 70));
          element.setAttribute("data-aos-duration", "650");
          element.setAttribute("data-aos-once", "true");
          changed = true;
        });
      });
      if (changed) AOS.refreshHard();
    }
    registerElements();
    AOS.init({ duration: 650, once: true, offset: 30 });
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(registerElements);
    });
    observer.observe(root, { childList: true, subtree: true });
    function updateMotion() {
      root.classList.toggle("career-page--reduced-motion", motion.matches);
    }
    updateMotion();
    motion.addEventListener("change", updateMotion);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      motion.removeEventListener("change", updateMotion);
    };
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame;
    function move(event) {
      if (motion.matches || event.pointerType === "touch") return;
      const bounds = hero.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width;
      const y = (event.clientY - bounds.top) / bounds.height;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        hero.style.setProperty("--pointer-x", `${x * 100}%`);
        hero.style.setProperty("--pointer-y", `${y * 100}%`);
        hero.style.setProperty("--drift-x", `${(x - .5) * 20}px`);
        hero.style.setProperty("--drift-y", `${(y - .5) * 20}px`);
      });
    }
    function reset() {
      cancelAnimationFrame(frame);
      ["--pointer-x", "--pointer-y", "--drift-x", "--drift-y"].forEach((name) => hero.style.removeProperty(name));
    }
    hero.addEventListener("pointermove", move);
    hero.addEventListener("pointerleave", reset);
    motion.addEventListener("change", reset);
    return () => {
      cancelAnimationFrame(frame);
      hero.removeEventListener("pointermove", move);
      hero.removeEventListener("pointerleave", reset);
      motion.removeEventListener("change", reset);
    };
  }, []);

  function openSection(name) {
    setSelectedSection(name);
    requestAnimationFrame(() => {
      contentRef.current?.scrollIntoView({ block: "start", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      contentRef.current?.focus({ preventScroll: true });
    });
  }

  return (
    <div className="career-page" ref={pageRef}>
      <CareerParticles />
      <SiteHeader>
        <li><NavLink end to="/">Home</NavLink></li>
        <li><NavLink end to="/about">About Us</NavLink></li>
        <li><NavLink end to="/career">Career</NavLink></li>
        <li><NavLink end to="/gallery">Gallery</NavLink></li>
        <li><NavLink end to="/login"><button type="button">Join Now</button></NavLink></li>
      </SiteHeader>
      <main className="career-hub">
        <div className="career-page-atmosphere" aria-hidden="true">
          {Array.from({ length: 6 }, (_, index) => <span key={index} style={{ "--float-top": `${8 + index * 16}%`, "--float-left": index % 2 ? "88%" : "-4%", "--float-delay": `${index * -3}s` }} />)}
        </div>
        <section className="career-hub-hero" ref={heroRef} aria-labelledby="career-hub-title">
          <div className="career-hero-atmosphere" aria-hidden="true">
            <span className="career-hero-ring career-hero-ring--outer" />
            <span className="career-hero-ring career-hero-ring--inner" />
            {Array.from({ length: 22 }, (_, index) => <i key={index} style={{ "--particle-x": `${(index * 37 + 11) % 100}%`, "--particle-y": `${(index * 23 + 7) % 100}%`, "--particle-size": `${2 + index % 3}px`, "--particle-delay": `${index * -.8}s`, "--particle-duration": `${8 + index % 5}s` }} />)}
          </div>
          <div className="career-hub-hero-copy">
            <span className="career-hub-eyebrow">YOUR NEXT CHAPTER</span>
            <h1 id="career-hub-title">Build skills.<br /><span>Find your next opportunity.</span></h1>
            <p>From academic records to your first role, explore the resources that help you move forward with confidence.</p>
            <div className="career-hub-actions">
              <button type="button" onClick={() => openSection("Jobs")}>Explore jobs <FaArrowRight /></button>
              <button className="career-hub-secondary" type="button" onClick={() => openSection("Placed Candidates")}>View placements</button>
            </div>
          </div>
          <div className="career-hub-hero-aside">
            <FaGraduationCap aria-hidden="true" />
            <h2>A clear path to your career</h2>
            <p>Verify records, discover roles, prepare for recruitment, and celebrate placement success.</p>
            <div><FaCheckCircle /> Academic support</div>
            <div><FaCheckCircle /> Career opportunities</div>
            <div><FaCheckCircle /> Placement community</div>
          </div>
        </section>

        <section className="career-hub-selector" aria-labelledby="career-sections-title">
          <div><span className="career-hub-eyebrow">CAREER RESOURCES</span><h2 id="career-sections-title">Choose your next step</h2><p>Everything you need, organized in one place.</p></div>
          <div className="career-hub-select-control">
            <label id="career-section-label" htmlFor="career-section-select">Career page sections</label>
            <GalleryEventSelect id="career-section-select" labelId="career-section-label" options={sections.map((section) => section.name)} value={selectedSection} onChange={setSelectedSection} />
          </div>
        </section>

        <section className="career-hub-content" ref={contentRef} tabIndex={-1} aria-labelledby="career-section-title">
          <div className="career-hub-content-heading"><span className="career-hub-section-icon"><Icon aria-hidden="true" /></span><div><h2 id="career-section-title">{current.name}</h2><p>{current.description}</p></div></div>
          <div key={selectedSection} className="career-hub-section-body">
            {selectedSection === "Jobs" && <JobList />}
            {selectedSection === "Placed Candidates" && <><p className="career-hub-data-note">The placement profiles below are demonstration content.</p><PlacementWall /></>}
            {selectedSection === "Student Result Verification" && <div className="career-verification-card">
              <span className="career-hub-status">Verification service not connected</span>
              <h3>Verify your academic records</h3>
              <p>Contact the college to confirm a student result. Keep the registration number, course, and examination year ready for the verification team.</p>
              <a className="career-hub-link" href={authorisedContact.telephone}>Authorised contact: {authorisedContact.phone} <FaArrowRight /></a>
            </div>}
            {selectedSection === "Jobs Recruitment" && <>
              <div className="career-recruitment-steps">
                {[{ title: "Prepare your profile", text: "Keep your academic details, contact information, and resume up to date." }, { title: "Find the right role", text: "Review qualifications, location, and experience requirements before applying." }, { title: "Submit your application", text: "Open a job listing and use its application form to share your details." }, { title: "Prepare for interviews", text: "Review the role, practice your skills, and follow the recruiter?s instructions." }].map((step, index) => <article key={step.title}><span>0{index + 1}</span><h3>{step.title}</h3><p>{step.text}</p></article>)}
              </div>
              <div className="career-recruitment-action"><div><h3>Ready to take the next step?</h3><p>Browse openings and apply for a role that fits your skills.</p></div><button type="button" onClick={() => openSection("Jobs")}>Browse jobs <FaArrowRight /></button></div>
            </>}
          </div>
        </section>
        <CampusFooter />
      </main>
    </div>
  );
}
